import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { MapPin, Star, SlidersHorizontal, Navigation, Loader, ChevronRight } from 'lucide-react';
import SkeletonCard from './SkeletonCard';
import { useTranslation } from 'react-i18next'; // 1. Import useTranslation
import { useAppContext } from '../App'; // <-- Import context
import { Heart } from 'lucide-react';   // <-- Import Heart
import { toast } from 'react-toastify';

const StadiumListing = ({ category, searchQuery, onSelectStadium, user }) => {
  const { favorites, toggleFavorite } = useAppContext();
  const { t, i18n } = useTranslation(); // 2. Add the translation hook
  const [stadiums, setStadiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filters, setFilters] = useState({
    city: 'all',
    minPrice: 0,
    maxPrice: 100,
    minRating: 0,
    sortBy: 'featured'
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadStadiums = useCallback(async () => {
    setLoading(true);
    try {
      let stadiumsQuery = collection(db, 'stadiums');
      if (filters.city !== 'all') {
        stadiumsQuery = query(stadiumsQuery, where('city', '==', filters.city));
      }
      if (filters.minRating > 0) {
        stadiumsQuery = query(stadiumsQuery, where('rating', '>=', filters.minRating));
      }
      const querySnapshot = await getDocs(stadiumsQuery);
      const stadiumsData = [];
      querySnapshot.forEach((doc) => {
        stadiumsData.push({ id: doc.id, ...doc.data() });
      });
      setStadiums(stadiumsData);
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
    setLoading(false);
  }, [filters.city, filters.minRating]);

  useEffect(() => {
    loadStadiums();
  }, [loadStadiums]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getUserLocation = () => {
    setLocationLoading(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setFilters({...filters, sortBy: 'distance'});
          setLocationLoading(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error(t('stadiumlisting.locationError'));
          setLocationLoading(false);
        }
      );
    } else {
      toast.error(t('stadiumlisting.geolocationError'));
      setLocationLoading(false);
    }
  };

  const stadiumsWithDistance = stadiums.map(stadium => {
    if (userLocation && stadium.coordinates) {
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        stadium.coordinates.lat,
        stadium.coordinates.lng
      );
      return { ...stadium, distance };
    }
    return stadium;
  });

  const filteredStadiums = stadiumsWithDistance.filter(stadium => {
    if (category !== 'all') {
      const hasSport = stadium.sports?.some(sport => sport.type === category);
      if (!hasSport) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = stadium.name.toLowerCase().includes(query);
      const matchesLocation = stadium.location.toLowerCase().includes(query);
      if (!matchesName && !matchesLocation) return false;
    }
    const prices = stadium.sports?.map(s => s.pricePerSlot) || [];
    if (prices.length === 0) return false;
    const minPrice = Math.min(...prices);
    if (minPrice < filters.minPrice || minPrice > filters.maxPrice) {
      return false;
    }
    return true;
  });

  const sortedStadiums = [...filteredStadiums].sort((a, b) => {
    switch(filters.sortBy) {
      case 'price':
        const priceA = Math.min(...a.sports.map(s => s.pricePerSlot));
        const priceB = Math.min(...b.sports.map(s => s.pricePerSlot));
        return priceA - priceB;
      case 'rating':
        return b.rating - a.rating;
      case 'distance':
        if (a.distance && b.distance) {
          return a.distance - b.distance;
        }
        return 0;
      case 'featured':
      default:
        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
    }
  });

  const cities = ['all', ...new Set(stadiums.map(s => s.city).filter(Boolean))];

  return (
    <div className={`min-h-screen bg-neutral-100 dark:bg-neutral-900 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* The global <Header> from App.js renders here.
        We have removed the old, duplicate header.
      */}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* --- NEW ACTION BAR --- */}
        <div className="flex gap-4 mb-6">
          {/* Show Filters Button (Mobile Only) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex lg:hidden items-center gap-2 px-4 py-3 bg-white dark:bg-neutral-800 text-primary-dark dark:text-primary-light rounded-xl shadow font-semibold"
          >
            <SlidersHorizontal size={20} />
            {t('listingpage.filters')}
          </button>
          
          {/* Near Me Button (Always visible) */}
          <button
            onClick={getUserLocation}
            disabled={locationLoading}
            className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-neutral-800 text-blue-600 dark:text-blue-400 rounded-xl shadow font-semibold disabled:opacity-50"
          >
            {locationLoading ? (
              <Loader size={20} className="animate-spin" />
            ) : (
              <Navigation size={20} />
            )}
            {t('listingpage.nearMe')}
          </button>
        </div>
        {/* --- END NEW ACTION BAR --- */}
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Filters Sidebar */}
          <div className={`${showFilters ? 'block' : 'hidden'} lg:block w-full lg:w-64 flex-shrink-0`}>
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 sticky top-28">
              <div className="flex items-center justify-between mb-6">
                {/* 4. Translate all text */}
                <h3 className="text-lg font-bold text-neutral-800 dark:text-white">{t('listingpage.filters')}</h3>
                <button
                  onClick={() => setFilters({ city: 'all', minPrice: 0, maxPrice: 100, minRating: 0, sortBy: 'featured' })}
                  className="text-sm text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary font-semibold"
                >
                  {t('listingpage.reset')}
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('listingpage.sortBy')}</label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="featured">{t('listingpage.sortFeatured')}</option>
                    <option value="price">{t('listingpage.sortPrice')}</option>
                    <option value="rating">{t('listingpage.sortRating')}</option>
                    {userLocation && <option value="distance">{t('listingpage.sortDistance')}</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('listingpage.city')}</label>
                  <select
                    value={filters.city}
                    onChange={(e) => setFilters({...filters, city: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {cities.map(city => (
                      <option key={city} value={city}>
                        {city === 'all' ? t('listingpage.allCities') : city}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                    {t('listingpage.priceRange', { min: filters.minPrice, max: filters.maxPrice })}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({...filters, maxPrice: parseInt(e.target.value)})}
                    className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer range-thumb:bg-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('listingpage.minRating')}</label>
                  <div className="flex gap-2">
                    {[0, 3, 4, 4.5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setFilters({...filters, minRating: rating})}
                        className={`flex-1 px-3 py-2 rounded-xl font-semibold transition ${
                          filters.minRating === rating
                            ? 'bg-primary text-white'
                            : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                        }`}
                      >
                        {rating === 0 ? t('listingpage.ratingAll') : `${rating}+`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stadium Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : sortedStadiums.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🏟️</div>
                <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-2">{t('listingpage.noVenues')}</h3>
                <p className="text-neutral-600 dark:text-neutral-400">{t('listingpage.noVenuesSub')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {sortedStadiums.map((stadium) => (
                  <button
                    key={stadium.id} 
                    onClick={() => onSelectStadium(stadium.id)}
                    className="group glass-card glass-card-hover rounded-2xl overflow-hidden text-left flex flex-col relative"
                  >
                    {/* Favorite Heart Button */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(stadium.id);
                      }}
                      className="absolute top-4 right-4 z-20 p-2 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm rounded-full hover:bg-white dark:hover:bg-neutral-700 transition shadow-luxury cursor-pointer"
                    >
                      <Heart 
                        size={20} 
                        className={`transition-colors ${favorites.includes(stadium.id) ? 'fill-red-500 text-red-500' : 'text-neutral-400 dark:text-neutral-500'}`} 
                      />
                    </div>
                    
                    {/* Image with h-56 to match Homepage */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={stadium.photos[0]}
                        alt={stadium.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                      
                      {stadium.distance && (
                        <div className="absolute top-4 left-4 bg-blue-500/95 backdrop-blur-sm text-white px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-1 shadow-luxury">
                          <Navigation size={14} />
                          {stadium.distance.toFixed(1)} km
                        </div>
                      )}
                      
                      {/* Glass Rating Badge - Match Homepage style */}
                      <div className="absolute bottom-4 left-4 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-4 py-2 rounded-full flex items-center gap-2 shadow-luxury border border-white/20">
                        <Star size={14} className="text-yellow-500 fill-yellow-500" />
                        <span className="font-bold text-neutral-800 dark:text-white">{stadium.rating}</span>
                        <span className="text-neutral-600 dark:text-neutral-400 text-sm">({stadium.reviewCount || 0})</span>
                      </div>
                    </div>
                    
                    {/* Content with p-6 to match Homepage */}
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-2 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">
                        {stadium.name}
                      </h3>
                      <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 mb-4">
                        <MapPin size={16} />
                        <span>{stadium.location}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {stadium.sports.slice(0, 3).map((sport, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary-300 rounded-full text-sm font-semibold"
                          >
                            {sport.type}
                          </span>
                        ))}
                        {stadium.sports.length > 3 && (
                          <span className="px-3 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-full text-sm font-semibold">
                            +{stadium.sports.length - 3}
                          </span>
                        )}
                      </div>

                      {/* Price section with pt-4 border to match Homepage */}
                      <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700 mt-auto">
                        <div>
                          <div className="text-sm text-neutral-600 dark:text-neutral-400">{t('listingpage.startingFrom')}</div>
                          <div className="text-2xl font-bold text-primary dark:text-primary-400">
                            ${Math.min(...stadium.sports.map(s => s.pricePerSlot))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-primary dark:text-primary-400 font-semibold">
                          <span>{t('listingpage.viewDetails')}</span>
                          <ChevronRight size={16} />
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StadiumListing;